import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { generateInvoice } from '../../api/invoices';
import { fetchOrder, downloadAmazonOrderDocument, updateI2iTracking } from '../../api/orders';
import ConfirmShipmentModal from '../procurement/ConfirmShipmentModal';
import {
  Badge,
  PageHeader,
  StatusBanner,
  inputClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from '../../components/ui';
import { CopyableValue } from '../../components/ui/CopyableValue';
import { OrderStatusBadge } from './orderStatus';
import { SellerCentralOrderDate } from './orderDateDisplay';
import { useNow } from './useNow';

function DetailRow({ label, value }) {
  const isText = typeof value === 'string' || value == null;
  return (
    <div className="grid gap-1">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`font-medium text-slate-900 ${isText ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function toShipDateInput(order) {
  if (!order?.shipByDate && !order?.orderDate) return '';
  return new Date(order.shipByDate || order.orderDate).toLocaleDateString('en-CA', {
    timeZone: 'Asia/Kolkata',
  });
}

function OrderDetailPage({ token, user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isUnshippedContext = location.pathname.startsWith('/admin/unshipped-orders/');
  const ordersListPath = isUnshippedContext ? '/admin/unshipped-orders' : '/admin/orders';
  const now = useNow();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [busyAction, setBusyAction] = useState('');
  const [reconfirmShipmentOpen, setReconfirmShipmentOpen] = useState(false);
  const [i2iDraft, setI2iDraft] = useState('');
  const [savingI2i, setSavingI2i] = useState(false);
  const [openingReconfirm, setOpeningReconfirm] = useState(false);
  const [editingI2i, setEditingI2i] = useState(false);

  const loadOrder = async () => {
    setStatus({ type: 'loading', message: '' });

    try {
      const payload = await fetchOrder(token, id);
      setOrder(payload.order);
      setI2iDraft(payload.order?.i2iTrackingId || '');
      setStatus({ type: 'ready', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const handleGenerateInvoice = async () => {
    setBusyAction('invoice');
    setStatus({ type: 'loading', message: '' });

    try {
      const payload = await generateInvoice(token, id);
      setStatus({ type: 'success', message: 'Invoice generated.' });
      navigate(`/admin/invoices/${payload.invoice.id}`);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setBusyAction('');
    }
  };

  const handleAmazonDocument = async (documentType) => {
    setBusyAction(documentType);
    setStatus({
      type: 'loading',
      message: 'Requesting Amazon Self Ship PDF…',
    });

    try {
      const payload = await downloadAmazonOrderDocument(token, id, documentType);
      setStatus({
        type: 'success',
        message: `Downloaded ${payload.filename}.`,
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setBusyAction('');
    }
  };

  const handleI2iSave = async () => {
    if (!order) return false;
    const nextValue = String(i2iDraft || '').trim();
    const previousValue = order.i2iTrackingId || '';
    if (nextValue === previousValue) {
      setI2iDraft(previousValue);
      setEditingI2i(false);
      return true;
    }

    setSavingI2i(true);
    try {
      const payload = await updateI2iTracking(token, order.id, nextValue);
      const savedOrder = payload.order || { ...order, i2iTrackingId: nextValue || null };
      setOrder(savedOrder);
      setI2iDraft(savedOrder.i2iTrackingId || '');
      setEditingI2i(false);
      setStatus({ type: 'success', message: 'I2I tracking saved.' });
      return true;
    } catch (error) {
      setI2iDraft(previousValue);
      setStatus({ type: 'error', message: error.message });
      return false;
    } finally {
      setSavingI2i(false);
    }
  };

  const handleReconfirmShipment = async (event) => {
    event.preventDefault();
    if (!order || openingReconfirm || !!busyAction) return;

    const tracking = String(i2iDraft || '').trim();
    if (!tracking) {
      setStatus({ type: 'error', message: 'Enter an I2I tracking ID before re-confirming shipment.' });
      return;
    }

    setOpeningReconfirm(true);
    try {
      const saved = await handleI2iSave();
      if (!saved) return;
      setReconfirmShipmentOpen(true);
    } finally {
      setOpeningReconfirm(false);
    }
  };

  if (status.type === 'loading' && !order) {
    return <p className="text-sm text-slate-500">Loading order...</p>;
  }

  if (!order) {
    return (
      <section className="grid gap-4">
        <StatusBanner
          status={{
            type: 'error',
            message: status.message || 'Order not found.',
          }}
          setStatus={setStatus}
        />
        <Link to={ordersListPath} className="font-semibold text-blue-700">
          Back to {isUnshippedContext ? 'unshipped orders' : 'orders'}
        </Link>
      </section>
    );
  }

  const i2iTrackingValue = String(i2iDraft || '').trim();
  const savedI2iTracking = String(order?.i2iTrackingId || '').trim();
  const i2iSyncedToAmazon =
    Boolean(savedI2iTracking) &&
    String(order?.trackingDummy || '').trim() === savedI2iTracking;
  const showI2iEditor = editingI2i || !i2iSyncedToAmazon;
  const shipDateInput = toShipDateInput(order);

  return (
    <section className="grid gap-5">
      <PageHeader
        title={order.amazonOrderId}
        subtitle={`${order.marketplace} marketplace order details`}
        actions={
          <>
            <Link to={ordersListPath} className={secondaryButtonClassName}>
              Back
            </Link>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={handleGenerateInvoice}
              disabled={!!busyAction}
            >
              Generate invoice
            </button>
          </>
        }
      />

      <StatusBanner status={status} setStatus={setStatus} />

      <div className="grid gap-4 lg:grid-cols-2">
        <article className={panelClassName}>
          <div className="border-b border-slate-200 px-5 py-4">
            <h3 className="text-base font-semibold text-slate-900">Buyer & shipping</h3>
          </div>
          <dl className="grid gap-4 px-5 py-5">
            {!order.buyerName && !order.buyerPhone && !order.shipAddressLine1 ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-950">
                Buyer details may be hidden by Amazon permissions. Re-sync after your SP-API app
                has the required PII roles, or use the sandbox order data if available.
              </p>
            ) : null}
            <DetailRow label="Buyer name" value={order.buyerName} />
            <DetailRow label="Phone" value={order.buyerPhone} />
            <DetailRow
              label="Address"
              value={
                [order.shipAddressLine1, order.shipAddressLine2]
                  .map((part) => (part || '').trim())
                  .filter(Boolean)
                  .join('\n') || null
              }
            />
            <DetailRow
              label="City / State / Pin"
              value={[order.shipCity, order.shipState, order.shipPincode]
                .filter(Boolean)
                .join(', ')}
            />
            <DetailRow
              label="Status"
              value={<OrderStatusBadge order={order} />}
            />
            <DetailRow
              label="Order date"
              value={
                order.orderDate ? (
                  <SellerCentralOrderDate
                    value={order.orderDate}
                    marketplace={order.marketplace}
                    now={now}
                  />
                ) : null
              }
            />
            <DetailRow
              label="Ship by"
              value={order.shipByDate ? new Date(order.shipByDate).toLocaleString() : null}
            />
            <DetailRow
              label="Expected delivery"
              value={order.deliveryDate ? new Date(order.deliveryDate).toLocaleString() : null}
            />
          </dl>
        </article>

        <article className={panelClassName}>
          <div className="border-b border-slate-200 px-5 py-4">
            <h3 className="text-base font-semibold text-slate-900">Tracking & shipment</h3>
          </div>
          <dl className="grid gap-4 px-5 py-5">
            <DetailRow label="US order ID" value={order.usOrderId} />
            <DetailRow label="US tracking ID" value={order.trackingUs} />
            <DetailRow label="Dummy tracking" value={order.trackingDummy} />
            <div className="grid gap-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                I2I tracking
              </dt>
              <dd className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                {showI2iEditor ? (
                  <input
                    type="text"
                    className={`${inputClassName} font-mono text-sm`}
                    placeholder="Enter I2I tracking ID"
                    value={i2iDraft}
                    disabled={savingI2i || !!busyAction}
                    onChange={(event) => setI2iDraft(event.target.value)}
                    onBlur={handleI2iSave}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape') {
                        setI2iDraft(savedI2iTracking);
                        setEditingI2i(false);
                      }
                    }}
                    autoFocus={editingI2i}
                  />
                ) : (
                  <span className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm font-semibold text-slate-900">
                    {savedI2iTracking}
                  </span>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {showI2iEditor ? (
                    <>
                      {editingI2i ? (
                        <button
                          type="button"
                          className={secondaryButtonClassName}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setI2iDraft(savedI2iTracking);
                            setEditingI2i(false);
                          }}
                          disabled={savingI2i || !!busyAction}
                        >
                          Cancel
                        </button>
                      ) : null}
                      {i2iTrackingValue ? (
                        <button
                          type="button"
                          className={primaryButtonClassName}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={handleReconfirmShipment}
                          disabled={!!busyAction || openingReconfirm || savingI2i}
                        >
                          {openingReconfirm ? 'Opening…' : 'Update tracking'}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <button
                      type="button"
                      className={secondaryButtonClassName}
                      onClick={() => {
                        setI2iDraft(savedI2iTracking);
                        setEditingI2i(true);
                      }}
                      disabled={!!busyAction}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </dd>
            </div>
            <DetailRow label="Weight (grams)" value={order.weightGrams} />
            <DetailRow label="HSN code" value={order.hsnCode} />
            <DetailRow label="HAWB #" value={order.hawbNumber} />
          </dl>
        </article>
      </div>

      {order.marketplace === 'IN' ? (
        <article className={panelClassName}>
          <div className="border-b border-slate-200 px-5 py-4">
            <h3 className="text-base font-semibold text-slate-900">Amazon packaging documents</h3>
            <p className="mt-1 text-sm text-slate-600">
              Same PDFs as Seller Central Self Ship — packing slip and tax invoice from Amazon&apos;s
              External Fulfillment API.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 px-5 py-5">
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={() => handleAmazonDocument('packing_slip')}
              disabled={!!busyAction}
            >
              {busyAction === 'packing_slip' ? 'Downloading...' : 'Print packing slip'}
            </button>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => handleAmazonDocument('tax_invoice')}
              disabled={!!busyAction}
            >
              {busyAction === 'tax_invoice' ? 'Downloading...' : 'Print tax invoice'}
            </button>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => handleAmazonDocument('shipping_label')}
              disabled={!!busyAction}
            >
              {busyAction === 'shipping_label' ? 'Downloading...' : 'Print shipping label'}
            </button>
            <a
              href={`https://sellercentral.amazon.in/orders-v3/order/${encodeURIComponent(order.amazonOrderId)}`}
              target="_blank"
              rel="noreferrer"
              className={secondaryButtonClassName}
            >
              Open in Seller Central
            </a>
          </div>
        </article>
      ) : null}

      <article className={panelClassName}>
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">Line items</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  ASIN (IN / COM)
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Qty
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Unit price
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Procurement
                </th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item) => (
                <tr key={item.id} className="border-b border-slate-200">
                  <td className="px-4 py-3 text-sm">
                    <strong className="block font-semibold">{item.title || item.sku || 'Item'}</strong>
                    {item.sku ? (
                      <CopyableValue label="SKU" value={item.sku} className="text-xs text-slate-500" />
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="grid gap-1">
                      {item.asinIn ? (
                        <CopyableValue label="IN" value={item.asinIn} />
                      ) : null}
                      {item.asinCom ? (
                        <CopyableValue label="COM" value={item.asinCom} />
                      ) : null}
                      {!item.asinIn && !item.asinCom ? '—' : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.quantityOrdered} ordered / {item.quantityShipped} shipped
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {order.currency} {Number(item.unitPrice).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {(() => {
                      if (item.procurementStatus === 'purchased') {
                        return <Badge tone="success">done</Badge>;
                      }
                      if (item.procurementStatus === 'in_procurement') {
                        return <Badge tone="info">in procurement</Badge>;
                      }
                      return <Badge tone="warning">pending</Badge>;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-200 px-5 py-4 text-right">
          <strong className="text-base text-slate-900">
            Total: {order.currency} {Number(order.totalAmount).toFixed(2)}
          </strong>
        </div>
      </article>

      <ConfirmShipmentModal
        isOpen={reconfirmShipmentOpen}
        onClose={() => setReconfirmShipmentOpen(false)}
        token={token}
        orderId={order.id}
        marketplace={order.marketplace || 'IN'}
        trackingNumber={i2iTrackingValue || order.i2iTrackingId || ''}
        amazonOrderId={order.amazonOrderId}
        initialShipDate={shipDateInput}
        initialCarrierCode={order.courierName || ''}
        title="Update Amazon tracking"
        trackingHint="Prefills from I2I tracking — edits tracking + carrier only; ship date stays as first confirmed."
        submitLabel="Update tracking"
        hideShipDate
        onSuccess={async (payload) => {
          setStatus({
            type: 'success',
            message: payload?.message || 'Tracking updated on Amazon Seller Central.',
          });
          setReconfirmShipmentOpen(false);
          setEditingI2i(false);
          await loadOrder();
        }}
      />
    </section>
  );
}

export default OrderDetailPage;
